import React, { useCallback, useRef, useEffect, useMemo } from "react";

import { useQuery } from "@apollo/client";
import { Container, Box, Typography, List } from "@mui/material";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import uniqBy from "lodash/uniqBy";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState, useRecoilValue } from "recoil";

import { TREES } from "api/queries";
import type { GraphQLError, Tree } from "api/types";
import { LineLoading } from "components";
import { modalWaterState, queryTreesVars } from "state/atoms";

import { StyledContainer } from "./components/HomePage.styles";
// Component
import HomeListSubHeader from "./components/HomeListSubHeader";
import HomePageTreeListItem from "./components/HomePageTreeListItem";

const HomePage = () => {
  const setModalWaterState = useSetRecoilState(modalWaterState);
  const treesVars = useRecoilValue(queryTreesVars);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // const { data: treeStats } = useQuery<{
  //   treeStats: TreeStat[];
  // }>(TREE_STATS);

  const { loading, data, fetchMore, error } = useQuery<{
    trees: Tree[];
  }>(TREES, { fetchPolicy: "network-only", variables: treesVars });

  const navigate = useNavigate();

  useEffect(() => {
    if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
      const err = error.graphQLErrors[0] as GraphQLError["graphQLErrors"][0];
      if (err?.status === 401) {
        navigate("/login");
      }
    }
  }, [error, navigate]);

  const lastTreeElRef = useCallback(
    (node: Element | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      observerRef.current = new IntersectionObserver(async (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && data?.trees && data?.trees.length) {
          try {
            await fetchMore({
              variables: { offset: data?.trees.length },
              updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult || (fetchMoreResult?.trees && fetchMoreResult?.trees.length === 0)) {
                  return prev;
                }
                return {
                  trees: uniqBy([...prev.trees, ...fetchMoreResult.trees], "id"),
                };
              },
            });
          } catch (error) {
            console.log("error: ", error);
          }
        }
      });
      if (node) {
        observerRef.current.observe(node);
      }
    },
    [data, fetchMore]
  );

  const trees = useMemo(() => {
    if (data?.trees && data.trees.length > 0) {
      return data.trees;
    }
    return [];
  }, [data]);

  const renderContent = useCallback(() => {
    if (!trees.length) {
      return (
        <Box display="flex" m={3} justifyContent="center">
          <SentimentVeryDissatisfiedIcon sx={{ fontSize: 40 }} />
          <Typography variant="h4" sx={{ ml: 2.5 }}>
            No data available
          </Typography>
        </Box>
      );
    }
    return trees.map((tree) => (
      <HomePageTreeListItem
        key={tree.id}
        ref={lastTreeElRef}
        tree={tree}
        daysDelayed={treesVars.daysDelayed}
        onStartWatering={(id) => {
          setModalWaterState((p) => ({ ...p, id, open: true }));
        }}
      />
    ));
  }, [trees, lastTreeElRef, treesVars.daysDelayed, setModalWaterState]);

  return (
    <>
      <LineLoading show={loading} />
      <StyledContainer component={Container} maxWidth="md">
        <Box sx={{ boxShadow: 1 }}>
          <List subheader={<HomeListSubHeader />}>{renderContent()}</List>
        </Box>
      </StyledContainer>
    </>
  );
};

export default HomePage;
